// Paper trades drawn on a chart: a marker at the fill and at the exit, the stop (a trailing stop as
// its path, stepping up) and the target while the trade is open. Used by the intraday and the days
// charts; every time given must be a candle time already on that chart's axis.
import { LineSeries, LineStyle, LineType, type IChartApi, type ISeriesApi, type SeriesMarker, type Time, type UTCTimestamp } from 'lightweight-charts';
import { palette } from './palette';

export interface TradeDraw {
  id: string;
  side: 'long' | 'short';
  entry: { t: number; price: number };
  exit?: { t: number; price: number; label: string };
  /** The stop while open (two points for a fixed stop; the path for a trailing one). */
  stop: { t: number; v: number }[];
  target?: { t: number; v: number }[];
  label: string;
}

const uniq = (pts: { t: number; v: number }[]) => {
  const out: { time: UTCTimestamp; value: number }[] = [];
  for (const p of [...pts].sort((a, b) => a.t - b.t)) {
    if (out.length && out[out.length - 1].time === p.t) out[out.length - 1].value = p.v;
    else out.push({ time: p.t as UTCTimestamp, value: p.v });
  }
  return out;
};

export class TradeLayer {
  private series: ISeriesApi<'Line'>[] = [];
  private list: TradeDraw[] = [];
  private sig = '';
  constructor(private chart: IChartApi) {}

  /** Draw these trades (an unchanged list is left alone); returns their markers to merge. */
  set(list: TradeDraw[]): SeriesMarker<Time>[] {
    const C = palette();
    const sig = JSON.stringify(list) + C.blue;
    if (sig !== this.sig) {
      this.sig = sig; this.list = list;
      for (const s of this.series) this.chart.removeSeries(s);
      this.series = [];
      for (const tr of list) {
        const stop = uniq(tr.stop);
        if (stop.length >= 2) {
          const s = this.chart.addSeries(LineSeries, { color: C.amber, lineWidth: 2, lineStyle: LineStyle.Solid, lineType: LineType.WithSteps, priceLineVisible: false, crosshairMarkerVisible: false, lastValueVisible: !tr.exit, title: !tr.exit ? 'Paper stop' : '' });
          s.setData(stop); this.series.push(s);
        }
        const target = tr.target ? uniq(tr.target) : [];
        if (target.length >= 2) {
          const s = this.chart.addSeries(LineSeries, { color: C.g, lineWidth: 2, lineStyle: LineStyle.Dotted, priceLineVisible: false, crosshairMarkerVisible: false, lastValueVisible: !tr.exit, title: !tr.exit ? 'Paper target' : '' });
          s.setData(target); this.series.push(s);
        }
      }
    }
    return this.markers();
  }

  markers(): SeriesMarker<Time>[] {
    const C = palette();
    const out: SeriesMarker<Time>[] = [];
    for (const tr of this.list) {
      const long = tr.side === 'long';
      out.push({ time: tr.entry.t as UTCTimestamp, position: long ? 'belowBar' : 'aboveBar', shape: long ? 'arrowUp' : 'arrowDown', color: C.blue, text: `Paper ${long ? 'buy' : 'sell'} ${tr.entry.price.toLocaleString('en-IN', { maximumFractionDigits: 2 })}` });
      if (tr.exit) out.push({ time: tr.exit.t as UTCTimestamp, position: long ? 'aboveBar' : 'belowBar', shape: 'square', color: C.blue, text: `${tr.exit.label} ${tr.exit.price.toLocaleString('en-IN', { maximumFractionDigits: 2 })}` });
    }
    return out;
  }
}
